import React, { useEffect, useState } from 'react'
import { withStyles } from '@mui/styles'
import { AttachMoney, ShoppingCart, SwapHoriz, TrendingFlat, FileCopyOutlined, Link, GavelOutlined, DeleteOutlined, LinkOffOutlined } from '@mui/icons-material'
import { Table, TableBody, TableHead, TableRow, IconButton, Tooltip, Badge, Typography } from '@mui/material'
import { muiOptions, MuiProps } from '../../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../../infrastructure/i18nextHelper'
import { Guid } from '../../../../infrastructure/guid'
import { popupNavigator } from '../../../../infrastructure/popupNavigator'
import { hasFeature, hasVesselDealDrivenFeature } from '../../../../infrastructure/feature'
import { Claims } from '../../../../infrastructure/signIn/models'
import { hasClaim } from '../../../../infrastructure/signIn/userContext'
import { TextField, NumberField, Select, DatePicker, DeltaNumberLabel } from '../../../common/customComponents'
import { commonStyle, Cell, RightAlignedCell } from './_common'
import { VesselEditContainer } from '../vesselEditStore'
import { PurchaseMovement, SitePurchaseMovement, MovementType } from '../../vesselModels'
import { PurchaseMovementTableProps } from './priceMovementsContainer'
import { Item } from '../../../common/components/select/types'
import { FormulaType, getFormulaTypes } from '../../../administration/masterData/pricingPeriod'

let tBase = 'vessels.label.purchaseMovement.'

type PricingNeedDeltasBySiteId = {
    [siteId: string]: number | null
}

function _PriceSeveralMovementTypesTable(props: PurchaseMovementTableProps & MuiProps & { purchaseMovementSite: string }) {
    let { classes, vesselProduct, purchaseMovement, isFixedPrice, duplicateSite, addSite,
        setPurchaseMovementToDelete, setSitePurchaseMovementToDelete, purchaseMovementSite } = props

    let initPricingNeedDeltas = (): PricingNeedDeltasBySiteId =>
        purchaseMovement.sites.reduce((acc, curr) => {
            acc[curr.id] = 0
            return acc
        }, {})
    let [pricingNeedDeltas, setPricingNeedDeltas] = useState<PricingNeedDeltasBySiteId>(initPricingNeedDeltas())
    let vessel = VesselEditContainer.useContainer()

    useEffect(() => {
        let deltas = initPricingNeedDeltas()
        for (var key in deltas)
            deltas[key] = pricingNeedDeltas[key] ?? 0

        setPricingNeedDeltas(deltas)
    }, [purchaseMovement.sites])

    let hasExpositionSimulation = hasFeature('VesselMultiplePricingPeriod')
        && !isFixedPrice
        && hasClaim(Claims.MarketRiskSimulatorReader)

    let hasMultiplePremium = hasFeature('VesselMultiplePremium') && hasFeature('VesselAutoCreateDeal') && !isFixedPrice
    let hasMultiplePricingPeriod = hasFeature('VesselMultiplePricingPeriod') && hasFeature('VesselAutoCreateDeal') && !isFixedPrice
    let hasFormulaType = hasFeature('FormulaType') && hasMultiplePricingPeriod
    let hasDynamicPricing = hasFeature('DynamicPricing') && hasMultiplePricingPeriod

    let movementTypes: Item[] =
        [
            { value: MovementType.PurchaseType.toString(), text: t(tBase + 'purchaseType') },
            { value: MovementType.SaleType.toString(), text: t(tBase + 'saleType') },
            { value: MovementType.TransferType.toString(), text: t(tBase + 'transferType') },
        ]

    let parseMovementType = (value: string): MovementType => {
        let type = parseInt(value)
        if (type == 2)
            return MovementType.TransferType
        if (type == 0)
            return MovementType.SaleType
        return MovementType.PurchaseType
    }

    let movementTypeIcon = (type: MovementType | null): JSX.Element | null => {
        if (type == null)
            return null
        if (type == MovementType.TransferType)
            return <SwapHoriz />
        if (type == MovementType.SaleType)
            return <AttachMoney />
        return <ShoppingCart />
    }

    let sites = (purchaseMvt: PurchaseMovement): Item[] => {
        if (!vessel.movementStock || !vessel.sites)
            return []

        let purchaseMovementStockSites = vessel.movementStock
            .filter(x => x.company === purchaseMvt.companyCode
                && x.dutyStatus === purchaseMvt.dutyStatus
                && x.productId === vesselProduct.productId)
            .map(x => x.site)
        return vessel.sites
            .filter(x => purchaseMovementStockSites.includes(x.code) && x.code != purchaseMovementSite)
            .map(x => ({ value: x.code, text: x.name }))
    }

    let changeDelta = (siteId: Guid, oldValue: number | null, newValue: number | null) => {
        let deltas = pricingNeedDeltas
        deltas[siteId] = (newValue ?? 0) - ((oldValue ?? 0) - (pricingNeedDeltas[siteId] ?? 0))
        setPricingNeedDeltas(deltas)
    }

    let updateQuantity = (purchaseMvt: PurchaseMovement, site: SitePurchaseMovement, newValue: number | null) => {
        if (vesselProduct.productUnit == 'TO')
            changeDelta(site.id, site.quantity, newValue)
        updateSite(purchaseMvt, site, site => { return { ...site, quantity: newValue, quantityOverride: newValue != null } })
    }

    let updateVolume = (purchaseMvt: PurchaseMovement, site: SitePurchaseMovement, newValue: number | null) => {
        if (vesselProduct.productUnit != 'TO')
            changeDelta(site.id, site.volume, newValue)
        updateSite(purchaseMvt, site, site => { return { ...site, volume: newValue, volumeOverride: newValue != null } })
    }

    let updateSite = (purchaseMvt: PurchaseMovement, site: SitePurchaseMovement, update: (site: SitePurchaseMovement) => SitePurchaseMovement) => {
        let siteIndex = purchaseMvt.sites.findIndex(x => x.id === site.id)
        purchaseMvt.sites[siteIndex] = update(site)
        vessel.setPurchaseMovement(vesselProduct.id, purchaseMvt)
    }

    let handleDeleteSite = (site: SitePurchaseMovement) => {
        let isLastSite = purchaseMovement.sites.length == 1
        if (isLastSite)
            setPurchaseMovementToDelete()
        else {
            setSitePurchaseMovementToDelete(site)
        }
    }

    let handleAssignDeal = async (site: SitePurchaseMovement | null, purchaseMovement: PurchaseMovement | null) => {
        await vessel.openAssignableDeals(site?.movementId!)
        vessel.setSitePurchaseMovementWithAssignedDeal({ purchaseMovementId: purchaseMovement?.id!, sitePurchaseMovementId: site?.id!, productId: vesselProduct.id })
    }

    let handleUnassignDeal = async (site: SitePurchaseMovement, purchaseMovement: PurchaseMovement) => {
        vessel.setSitePurchaseMovementWithAssignedDeal({ purchaseMovementId: purchaseMovement.id!, sitePurchaseMovementId: site?.id!, productId: vesselProduct.id })
        vessel.setUnassignDealContext({ dealId: site.dealId, movementId: site.movementId })
        vessel.setUnassignDealConfirmDialogOpen(true)
    }

    let shouldDisplayDelta = (siteId: string): boolean =>
        siteId in pricingNeedDeltas
        && siteId in vessel.pricingNeeds
        && vessel.pricingNeeds[siteId] !== null
    let isVolume = vesselProduct.productUnit !== 'TO'

    let getHighlightedDates = () =>
        vessel.quotationCalendars[vesselProduct.priceIndexCode ?? '']?.map(date => ({ date: date, color: '#DB7056' })) || []

    return (
        <>
            <Table style={{ tableLayout: 'auto' }} size='small' sx={{ '& td, & th': { paddingLeft: '4px', paddingRight: '4px' } }}>
                <TableHead>
                    <TableRow>
                        <Cell>{t(tBase + 'movementType')}</Cell>
                        <Cell>{t(tBase + 'destination')}</Cell>
                        {hasFeature('PurchaseMovementTransporter') && <Cell>{t(tBase + 'transporter')}</Cell>}
                        <Cell>{t(tBase + 'availabilityDate')}</Cell>
                        {hasFeature('ReleaseDate') && <Cell>{t(tBase + 'releaseDate')}</Cell>}
                        <Cell>{t(tBase + 'quantityUnit')}</Cell>
                        {isVolume && <Cell>{t(tBase + 'volumeUnit', { unit: vesselProduct.productUnit })}</Cell>}
                        {hasDynamicPricing && <Cell>{t(tBase + 'deemedDate')}</Cell>}
                        {hasDynamicPricing && <Cell>{t(tBase + 'pricingPeriodRule')}</Cell>}
                        {hasMultiplePricingPeriod && <Cell>{t(tBase + 'pricingFrom')}</Cell>}
                        {hasMultiplePricingPeriod && <Cell>{t(tBase + 'pricingTo')}</Cell>}
                        {hasMultiplePremium && <Cell>{t(tBase + 'premium', { unit: vesselProduct.premiumCurrency })}</Cell>}
                        {hasFormulaType && <Cell>{t(tBase + 'formulaType')}</Cell>}
                        {vessel.isAuthorized('aseReference') && <Cell>{t(tBase + 'aseReference')}</Cell>}
                        {hasExpositionSimulation && <Cell>{t(tBase + 'simulationExposition')}</Cell>}
                        {hasFeature('PurchaseMovementTransporter') && <Cell>{t(tBase + 'purchaseOrder')}</Cell>}
                        <Cell></Cell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {purchaseMovement.sites.map((site, i) =>
                        <TableRow className={classes.sitePurchaseMvtRow} key={i}>
                            <Cell key={'movementType'}>
                                <Select
                                    variant={'standard'}
                                    disabled={site.movementId != null}
                                    classesOverride={{ form: classes.select, select: 'site-select' }}
                                    value={site.movementType}
                                    choices={movementTypes}
                                    startAdornment={movementTypeIcon(site.movementType) ?? undefined}
                                    onChange={val =>
                                        updateSite(purchaseMovement, site, s => { return { ...s, movementType: parseMovementType(val), transferDestinationSite: null } })
                                    } />
                            </Cell>
                            <Cell key={'destination'}>
                                {site.movementType == MovementType.TransferType
                                    ? <Select variant={'standard'}
                                        classesOverride={{ form: classes.purchaseMovementHeaderCombination, select: 'site-select' }}
                                        value={site.transferDestinationSite}
                                        choices={sites(purchaseMovement)}
                                        onChange={val =>
                                            updateSite(purchaseMovement, site, s => { return { ...s, transferDestinationSite: val } })
                                        } />
                                    : (site.movementType == MovementType.SaleType && hasFeature('VesselAutoCreateDeal'))
                                        ? <Select variant={'standard'}
                                            classesOverride={{ form: classes.select, select: 'site-select' }}
                                            value={site.counterpartyId}
                                            choices={vessel.salesCounterpartys[purchaseMovement.companyCode].map(x => ({ value: x.id, text: x.name }))}
                                            onChange={val =>
                                                updateSite(purchaseMovement, site, s => { return { ...s, counterpartyId: val } })
                                            } />
                                        : undefined
                                }
                            </Cell>
                            {hasFeature('PurchaseMovementTransporter') && <Cell key={'transporter'}>
                                <Select
                                    variant={'standard'}
                                    classesOverride={{ form: classes.select }}
                                    value={site.transporterId}
                                    choices={vessel.transporters[purchaseMovement.companyCode].map(x => { return { value: x.id, text: x.name } })}
                                    onChange={val =>
                                        updateSite(purchaseMovement, site, s => { return { ...s, transporterId: val } })} />
                            </Cell>}
                            <Cell key={'availabilityDate'}>
                                <DatePicker classesOverride={{ datepicker: classes.dateInput }}
                                    disableNewStyle
                                    date={site.availabilityDate}
                                    auto={!site.availabilityDateOverwritten}
                                    label={''}
                                    setDate={(newDate: string | null) =>
                                        updateSite(purchaseMovement, site, s => {
                                            return { ...s, availabilityDate: newDate, availabilityDateOverwritten: newDate != null }
                                        })}
                                />
                            </Cell>
                            {hasFeature('ReleaseDate') && <Cell key={'releaseDate'}>
                                <DatePicker classesOverride={{ datepicker: classes.dateInput }}
                                    disableNewStyle
                                    date={site.releaseDate}
                                    auto={!site.releaseDateOverwritten}
                                    label={''}
                                    setDate={(newDate: string | null) =>
                                        updateSite(purchaseMovement, site, s => ({
                                            ...s,
                                            releaseDate: newDate,
                                            releaseDateOverwritten: newDate != null
                                        })
                                        )}
                                />
                            </Cell>}
                            <Cell key={'quantity'}>
                                <NumberField disableNewStyle
                                    key={site.id} label={''}
                                    overrideStyle={{ root: classes.siteInput }}
                                    onChange={vol => updateQuantity(purchaseMovement, site, vol)}
                                    text={site.quantity}
                                    precision={3} auto={!site.quantityOverride} />
                            </Cell>
                            {isVolume && <Cell key={'volume'}>
                                <NumberField disableNewStyle
                                    key={site.id} label={''}
                                    overrideStyle={{ root: classes.siteInput }}
                                    onChange={vol => updateVolume(purchaseMovement, site, vol)}
                                    text={site.volume}
                                    precision={3} auto={!site.volumeOverride} />
                            </Cell>}
                            {hasDynamicPricing &&
                                <Cell key={'deemedDate'}>
                                    {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                        ? <DatePicker classesOverride={{ datepicker: classes.dateInput }}
                                            disableNewStyle
                                            date={site.deemedDate}
                                            label={''}
                                            setDate={(newDate: string | null) =>
                                                updateSite(purchaseMovement, site, s => {
                                                    return { ...s, deemedDate: newDate }
                                                })}
                                        /> : undefined}
                                </Cell>}
                            {hasDynamicPricing &&
                                <Cell key={'pricingPeriodRule'}>
                                    {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                        ? <Select label={''}
                                            isClearable={true}
                                            clearableLabel={'None'}
                                            value={site.pricingPeriodRuleId}
                                            variant={'standard'}
                                            classesOverride={{ form: classes.select }}
                                            choices={vessel.pricingPeriodRules.map(x => ({ value: x.id, text: x.pricingPeriod }))}
                                            onChange={val => updateSite(purchaseMovement, site, s => { return { ...s, pricingPeriodRuleId: val } })} /> : undefined}
                                </Cell>}
                            {hasMultiplePricingPeriod &&
                                <Cell key={'pricingFrom'}>
                                    {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                        ? <DatePicker classesOverride={{ datepicker: classes.dateInput }}
                                            disableNewStyle
                                            date={site.pricingFrom}
                                            getHighlightedDates={getHighlightedDates}
                                            label={''}
                                            disabled={!!site.dealId && !!site.purchaseOrder}
                                            auto={hasDynamicPricing && !site.pricingFromOverwritten}
                                            setDate={(newDate: string | null) =>
                                                updateSite(purchaseMovement, site, s => {
                                                    return { ...s, pricingFrom: newDate, pricingFromOverwritten: !!newDate }
                                                })}
                                        /> : undefined}
                                </Cell>}
                            {hasMultiplePricingPeriod && <Cell key={'pricingTo'}>
                                {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                    ? <DatePicker classesOverride={{ datepicker: classes.dateInput }}
                                        disableNewStyle
                                        date={site.pricingTo}
                                        getHighlightedDates={getHighlightedDates}
                                        label={''}
                                        disabled={!!site.dealId && !!site.purchaseOrder}
                                        auto={hasDynamicPricing && !site.pricingToOverwritten}
                                        setDate={(newDate: string | null) =>
                                            updateSite(purchaseMovement, site, s => {
                                                return { ...s, pricingTo: newDate, pricingToOverwritten: !!newDate }
                                            })}
                                    /> : undefined}
                            </Cell>}
                            {hasMultiplePremium && <Cell key={'premium'}>
                                {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                    ? <NumberField disableNewStyle
                                        key={site.id} label={''}
                                        overrideStyle={{ root: classes.siteInput }}
                                        disabled={!!site.dealId && !!site.purchaseOrder}
                                        onChange={vol => updateSite(purchaseMovement, site, s => { return { ...s, premium: vol } })}
                                        text={site.premium}
                                        precision={3}
                                        allowNegative={true} /> : undefined}
                            </Cell>}
                            {hasFormulaType && <Cell key={'formulaType'}>
                                {site.movementType == MovementType.SaleType || site.movementType == MovementType.PurchaseType
                                    ? <Select label={''}
                                        isClearable={true}
                                        clearableLabel={'None'}
                                        value={site.formulaType}
                                        variant={'standard'}
                                        classesOverride={{ form: classes.select }}
                                        choices={getFormulaTypes()}
                                        onChange={val => updateSite(purchaseMovement, site, s => { return { ...s, formulaType: val as FormulaType } })} />
                                    : undefined}
                            </Cell>}
                            {vessel.isAuthorized('aseReference') && <Cell key={'aseReference'}>
                                <TextField disableNewStyle
                                    label={''}
                                    overrideStyle={{ root: classes.siteInput }}
                                    onChange={e =>
                                        updateSite(purchaseMovement, site, site => { return { ...site, aseReference: e.target.value } })}
                                    text={site.aseReference} />
                            </Cell>}
                            {hasExpositionSimulation &&
                                <Cell key={'simulationExposition'}>{
                                    shouldDisplayDelta(site.id)
                                        ? <DeltaNumberLabel
                                            value={vessel.pricingNeeds[site.id]!}
                                            delta={pricingNeedDeltas[site.id]}
                                            align={'left'} />
                                        : <span>{t(tBase + 'noPricingNeedDelta')}</span>
                                }
                                </Cell>}
                            {hasFeature('PurchaseMovementTransporter') && <Cell key={'purchaseOrder'}>
                                <TextField disableNewStyle label={''}
                                    disabled={hasVesselDealDrivenFeature()}
                                    overrideStyle={{ root: classes.siteInput }}
                                    onChange={e =>
                                        updateSite(purchaseMovement, site, site => { return { ...site, purchaseOrder: e.target.value } })}
                                    text={site.purchaseOrder} />
                            </Cell>}
                            <RightAlignedCell key={'buttons'}>
                                <div>
                                    {!!site.dealId
                                        ? <Tooltip title={<Typography variant='body1'>{t(tBase + 'goToDeal', { dealNumber: site.dealNumber })}</Typography>} placement='top'>
                                            <IconButton className={classes.iconButton} aria-label='go to deal' size={'small'}
                                                onClick={() => popupNavigator.open('deal', site.dealId)}>
                                                <GavelOutlined className='site-open-deal-icon' />
                                            </IconButton>
                                        </Tooltip>
                                        : !!site.movementId && (site.movementType == MovementType.PurchaseType || site.movementType == MovementType.SaleType) && !hasFeature('VesselAutoCreateDeal')
                                            ? <IconButton className={classes.iconButton} aria-label='assign deal' size={'small'}
                                                onClick={() => handleAssignDeal(site, purchaseMovement)}>
                                                <Badge badgeContent={<Link />}><GavelOutlined className='site-open-deal-icon' /></Badge>
                                            </IconButton>
                                            : undefined}
                                    {!!site.movementId
                                        ? <Tooltip title={<Typography variant='body1'>{t(tBase + 'goToMovement')}</Typography>} placement='top'>
                                            <IconButton className={classes.iconButton} aria-label='go to movement' size={'small'}
                                                onClick={() => popupNavigator.open('movement', site.movementId)}>
                                                <TrendingFlat className='site-open-movement-icon' />
                                            </IconButton>
                                        </Tooltip>
                                        : undefined}
                                    {!!site.dealId && !hasFeature('VesselAutoCreateDeal') && !hasFeature('AutoCreateDealOnPurchase') ?
                                        <Tooltip title={<Typography variant='body1'>{t(tBase + 'unassignDeal', { dealNumber: site.dealNumber })}</Typography>} placement='top'>
                                            <IconButton className={classes.iconButton} aria-label='unassign deal' size={'small'}
                                                onClick={() => handleUnassignDeal(site, purchaseMovement)}>
                                                <LinkOffOutlined className='site-open-deal-icon' />
                                            </IconButton>
                                        </Tooltip>
                                        : undefined
                                    }
                                    {!isFixedPrice
                                        ? <Tooltip title={<Typography variant='body1'>{t(tBase + 'duplicate')}</Typography>} placement='top'>
                                            <IconButton className={classes.iconButton} aria-label='duplicate' size={'small'}
                                                onClick={() => addSite(purchaseMovement, duplicateSite(site))}>
                                                <FileCopyOutlined />
                                            </IconButton>
                                        </Tooltip>
                                        : undefined
                                    }
                                    <IconButton onClick={() => handleDeleteSite(site)} size={'small'}><DeleteOutlined /></IconButton>
                                </div>
                            </RightAlignedCell>
                        </TableRow>)}
                </TableBody>
            </Table>
        </>)
}

export let PriceSeveralMovementTypesTable = withStyles(commonStyle, muiOptions)(_PriceSeveralMovementTypesTable)